import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { IMG } from "@/lib/assets";
import { KINDS, today } from "@/lib/perform";
import { Swords, Lock, ArrowRight, Gauge, Medal } from "lucide-react";

// Dashboard hero card: today's workout, your score status and your fitness level at a glance
export function TodayCard() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["wods", today()], queryFn: async () => (await api.get("/api/wods", { params: { day: today() } })).data });
  const { data: fl } = useQuery({ queryKey: ["fitness-level"], queryFn: async () => (await api.get("/api/performance/fitness-level")).data });
  const w = data?.wods?.[0];
  const scored = (w?.my_scores || []).length;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="today-card">
      <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate("/app/today")}
        className="lg:col-span-2 group relative overflow-hidden rounded-2xl border border-acid/30 text-left min-h-[13rem]">
        <img src={IMG.conditioning} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#050505 25%,rgba(5,5,5,0.55) 70%,rgba(5,5,5,0.2))" }} />
        <div className="relative p-6 flex flex-col h-full justify-between gap-4">
          <div>
            <p className="mono-label text-acid flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> today's WOD{w?.club_name ? ` · ${w.club_name}` : ""}</p>
            {!w ? (
              <>
                <p className="font-heading font-black text-3xl mt-1">Nothing programmed yet</p>
                <p className="text-sm text-[#B7BECB] mt-1">Plan a session or log one you've done. It goes straight onto your calendar.</p>
              </>
            ) : w.hidden ? (
              <>
                <p className="font-heading font-black text-3xl mt-1 flex items-center gap-2"><Lock className="w-6 h-6 text-acid" /> Revealed soon</p>
                <p className="text-sm text-[#B7BECB] mt-1">Your coach keeps today's workout under wraps until {new Date(w.publish_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}.</p>
              </>
            ) : (
              <>
                <p className="font-heading font-black text-3xl mt-1">{w.title}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {w.sections.map((s, i) => (
                    <span key={i} className="text-xs rounded-full px-2.5 py-1 border" style={{ borderColor: `${(KINDS[s.kind] || KINDS.metcon).c}66`, color: (KINDS[s.kind] || KINDS.metcon).c }}>{s.title}</span>
                  ))}
                </div>
              </>
            )}
          </div>
          <span className="inline-flex items-center gap-2 self-start rounded-full h-10 px-5 bg-acid text-black font-medium">
            {w && !w.hidden ? (scored ? `Logged ${scored}: see the whiteboard` : "Log your score") : "Open"} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </motion.button>
      <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} onClick={() => navigate(fl?.overall ? "/app/performance" : "/app/benchmarks")}
        className="relative overflow-hidden rounded-2xl border border-[#1E222A] bg-[#0F1115] p-6 text-left group">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-25 group-hover:opacity-40" style={{ background: "#3D7BFF" }} />
        <p className="relative mono-label flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-acid" /> fitness level</p>
        <p className="relative font-heading font-black text-6xl mt-2" style={{ background: "linear-gradient(90deg,#3D7BFF,#CCFF00)", WebkitBackgroundClip: "text", color: "transparent" }}>{fl?.overall ?? "–"}</p>
        <p className="relative text-sm text-[#8A91A6] mt-1">{fl?.overall ? "out of 100, from your best results" : "Log a few benchmarks to get your level"}</p>
        <span className="relative inline-flex items-center gap-1.5 text-sm text-acid mt-4"><Medal className="w-4 h-4" /> {fl?.overall ? "See strengths and gaps" : "Go to Benchmarks"}</span>
      </motion.button>
    </div>
  );
}
